import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { StaffDetail, getStaffDetail } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { can } from "../../utils/roles";
import { FaLock, FaSyncAlt, FaPrint, FaArrowLeft } from "react-icons/fa";

/**
 * One staff member: where they stand today, and everything they have booked.
 *
 * The limit balance is the same live figure the agent sees on their own booking
 * screen, so a super user looking at somebody and that person looking at
 * themselves never see two different numbers.
 *
 * The booking list is deliberately unfiltered and undated — the spec asks for
 * the list itself, not a search over it.
 */

const errorOf = (e: any, fallback: string) =>
    e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.error ?? fallback;

const money = (n: number | null, currency = "AED") =>
    n === null ? "—" : `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const dubaiDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Dubai",
    });

/**
 * What actually happened to a booking, in one phrase.
 *
 * Cancellation is checked first: a refunded booking that was also ticketed is a
 * refund, and saying "Ticketed" about it would be wrong in the way that matters.
 */
function statusOf(b: StaffDetail["bookings"][number]): { label: string; tone: string } {
    if (["full", "refunded", "void"].includes(b.cancellationStatus)) {
        return { label: "Refunded", tone: "bg-gray-100 text-gray-600" };
    }
    if (b.cancellationStatus === "partial") {
        return { label: "Part cancelled", tone: "bg-amber-100 text-amber-800" };
    }
    // Checked before "awaiting payment", which is what an expired booking would
    // otherwise read as forever — it is unpaid, and now it always will be.
    if (b.status === "expired") {
        return { label: "Expired unpaid", tone: "bg-gray-100 text-gray-600" };
    }
    if (b.paymentStatus !== "completed") {
        return { label: "Awaiting payment", tone: "bg-amber-100 text-amber-800" };
    }
    if (b.ticketingStatus === "failed") {
        return { label: "Ticketing failed", tone: "bg-red-100 text-red-700" };
    }
    if (b.ticketingStatus === "completed") {
        return { label: "Ticketed", tone: "bg-green-100 text-green-700" };
    }
    return { label: "Ticketing", tone: "bg-blue-100 text-blue-700" };
}

const StaffSales: React.FC = () => {
    const { uid } = useParams<{ uid: string }>();
    const { profile } = useAuth();
    const navigate = useNavigate();

    const [data, setData] = useState<StaffDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const allowed = can(profile, "dashboard");

    const load = useCallback(async () => {
        if (!allowed || !uid) return;
        setLoading(true);
        try {
            setData(await getStaffDetail(uid));
            setError("");
        } catch (e: any) {
            setError(errorOf(e, "Could not load this person."));
        } finally {
            setLoading(false);
        }
    }, [allowed, uid]);

    useEffect(() => { load(); }, [load]);

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">Only head office can see staff sales.</p>
                </div>
            </div>
        );
    }

    const l = data?.limit;
    const pct = l && l.dailyLimit > 0
        ? Math.min(100, Math.round((l.used / l.dailyLimit) * 100))
        : 0;
    const tone = !l ? null : l.blocked
        ? { bar: "bg-red-500", box: "border-red-200 bg-red-50", text: "text-red-700" }
        : l.warning
            ? { bar: "bg-amber-500", box: "border-amber-200 bg-amber-50", text: "text-amber-800" }
            : { bar: "bg-primary", box: "border-gray-200 bg-white", text: "text-gray-600" };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 print:max-w-none print:px-0">
            <button
                onClick={() => navigate("/admin/dashboard")}
                className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 print:hidden"
            >
                <FaArrowLeft /> Back to sales
            </button>

            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                        {data?.staff.displayName || data?.staff.email || "…"}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {data ? `${data.staff.email} · ${data.staff.officeId ?? "no branch"}` : ""}
                    </p>
                </div>
                <div className="flex gap-2 print:hidden">
                    <button
                        onClick={() => window.print()}
                        disabled={loading || !data}
                        className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <FaPrint /> PDF
                    </button>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading && !data && <div className="py-16 text-center text-gray-400">Loading…</div>}

            {/* ── Today's limit ── */}
            {l && tone && (
                <div className={`rounded-xl border p-4 mb-6 ${tone.box}`}>
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                        <span className={`text-sm font-medium ${tone.text}`}>
                            {!l.limited
                                ? "Not subject to the daily limit"
                                : l.blocked
                                    ? "At today's limit"
                                    : `Today: ${money(l.used, l.currency)} of ${money(l.dailyLimit, l.currency)}`}
                        </span>
                        {l.limited && !l.blocked && (
                            <span className="text-xs text-gray-500">
                                {money(l.remaining, l.currency)} left
                            </span>
                        )}
                    </div>
                    {l.limited && (
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                            <div className={`h-full ${tone.bar} transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                    )}
                    {l.limited && (
                        <p className="mt-2 text-xs text-gray-500">
                            Resets at midnight Dubai time. Head office can reset it sooner or change
                            the limit from the Staff screen.
                        </p>
                    )}
                </div>
            )}

            {/* ── Their bookings ── */}
            {data && (
                <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 text-sm font-medium text-gray-900">
                        Bookings ({data.bookings.length})
                    </div>

                    {data.bookings.length === 0 ? (
                        <div className="px-4 py-10 text-center text-gray-400 text-sm">
                            Nothing booked yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs text-gray-500 bg-gray-50">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium">PNR</th>
                                        <th className="text-left px-4 py-2 font-medium">Route</th>
                                        <th className="text-right px-4 py-2 font-medium">Pax</th>
                                        <th className="text-left px-4 py-2 font-medium">Travel</th>
                                        <th className="text-right px-4 py-2 font-medium">Amount</th>
                                        <th className="text-left px-4 py-2 font-medium">Paid by</th>
                                        <th className="text-left px-4 py-2 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.bookings.map((b) => {
                                        const s = statusOf(b);
                                        return (
                                            <tr key={b.bookingId}>
                                                <td className="px-4 py-2.5 font-mono text-xs text-gray-900">
                                                    {b.pnr ?? "—"}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-700">
                                                    {b.origin && b.destination ? `${b.origin} → ${b.destination}` : "—"}
                                                    {b.airlineCode && (
                                                        <span className="ml-2 text-xs text-gray-400">{b.airlineCode}</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-right text-gray-500">
                                                    {b.passengerCount ?? "—"}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-500">
                                                    {b.travelDate ?? "—"}
                                                </td>
                                                <td className="px-4 py-2.5 text-right font-medium">
                                                    {money(b.amount, b.currency)}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-500">
                                                    {b.paymentChannel === "cash" ? "Cash" : b.paymentChannel ?? "—"}
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${s.tone}`}>
                                                        {s.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {data && (
                <p className="mt-4 text-xs text-gray-500">
                    Their whole list, newest first — website bookings only. Booked{" "}
                    {data.bookings.length > 0 ? `most recently ${dubaiDate(data.bookings[0].createdAt)}` : "nothing yet"}.
                </p>
            )}
        </div>
    );
};

export default StaffSales;
